/**
 * Hourtrix local-notifications scheduler.
 *
 * Wraps @capacitor/local-notifications into a small, no-op-on-web API that
 * the rest of the app can call without any platform checks.
 *
 * Two notification families:
 *   - DAILY REMINDERS (morning / midday / evening / daily_review) — fixed-ID,
 *     repeat daily at wall-clock HH:MM derived from the user's notification
 *     preferences. IDs 10001..10004.
 *   - ACTIVITY REMINDERS — one-off, fire `reminder_minutes_before` an activity's
 *     `preferred_time` today. IDs derived from a stable hash of activity_id
 *     (range 20000..29999).
 *
 * The module is loaded dynamically so the plugin never lands in the web bundle.
 *
 * Design tenets (per playbook):
 *   - Use `allowWhileIdle: true` so Doze mode doesn't silently swallow the alert.
 *   - Keep reminder IDs stable per-slot / per-activity so we can cancel cleanly.
 *   - Reconcile from the user's current prefs on app-start and after login —
 *     never trust the OS-scheduled list to be in sync with prefs on its own.
 */
import { isNative } from './native';

const DAILY_IDS = {
  morning: 10001,
  midday: 10002,
  evening: 10003,
  daily_review: 10004,
};
const DAILY_ID_VALUES = Object.values(DAILY_IDS);

const ACTIVITY_ID_MIN = 20000;
const ACTIVITY_ID_MAX = 29999;

/** Stable, bounded ID derived from the activity_id string. */
function activityIdToNotificationId(activityId) {
  let hash = 0;
  for (let i = 0; i < activityId.length; i++) {
    hash = ((hash << 5) - hash + activityId.charCodeAt(i)) | 0;
  }
  const range = ACTIVITY_ID_MAX - ACTIVITY_ID_MIN + 1;
  return ACTIVITY_ID_MIN + Math.abs(hash) % range;
}

async function getPlugin() {
  if (!isNative()) return null;
  try {
    const mod = await import('@capacitor/local-notifications');
    return mod.LocalNotifications;
  } catch (e) {
    return null;
  }
}

/**
 * Check OS permission state. Returns 'granted' | 'denied' | 'prompt'.
 * Returns 'granted' on web so callers don't branch.
 */
export async function checkNotificationPermission() {
  const LN = await getPlugin();
  if (!LN) return 'granted';
  try {
    const res = await LN.checkPermissions();
    return res.display || 'prompt';
  } catch (e) {
    return 'prompt';
  }
}

/** Prompt the user for notification permission (no-op on web). */
export async function requestNotificationPermission() {
  const LN = await getPlugin();
  if (!LN) return 'granted';
  try {
    const res = await LN.requestPermissions();
    return res.display || 'denied';
  } catch (e) {
    return 'denied';
  }
}

function parseHHMM(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) {
    return null;
  }
  return { hour: h, minute: m };
}

function buildDailyNotifications(prefs) {
  const source = prefs || {};
  const slots = [
    { id: DAILY_IDS.morning, enabled: source.morning_enabled, time: source.morning_time,
      title: 'Morning plan', body: "Let's set the tone. Here's your day at a glance." },
    { id: DAILY_IDS.midday, enabled: source.midday_enabled, time: source.midday_time,
      title: 'Midday check-in', body: 'How is your plan holding up? A quick log keeps the streak going.' },
    { id: DAILY_IDS.evening, enabled: source.evening_enabled, time: source.evening_time,
      title: 'Evening wrap-up', body: "A few minutes now saves tomorrow's guesswork. Log what you did." },
    { id: DAILY_IDS.daily_review, enabled: true, time: source.daily_review_time,
      title: 'Daily review', body: 'Close today and teach Hourtrix what worked.' },
  ];

  return slots
    .filter((s) => s.enabled && s.time)
    .map((s) => {
      const t = parseHHMM(s.time);
      if (!t) return null;
      return {
        id: s.id,
        title: s.title,
        body: s.body,
        schedule: { on: { hour: t.hour, minute: t.minute }, allowWhileIdle: true },
      };
    })
    .filter(Boolean);
}

/**
 * Replace all 4 daily reminders with a schedule derived from `prefs`.
 * Cancels any existing daily ids first so toggling one off actually unschedules.
 */
export async function reconcileDailyReminders(prefs) {
  const LN = await getPlugin();
  if (!LN) return;
  try {
    // Clear all four daily slots first — unconditional.
    await LN.cancel({ notifications: DAILY_ID_VALUES.map((id) => ({ id })) });
  } catch (e) { /* ignore */ }

  const scheduled = buildDailyNotifications(prefs);
  if (!scheduled.length) return;
  try {
    await LN.schedule({ notifications: scheduled });
  } catch (e) {
    // Swallow — the UI already shows the prefs as saved; a missed schedule is
    // recovered on next app-open via reconcileAll().
    // eslint-disable-next-line no-console
    console.warn('reconcileDailyReminders failed', e);
  }
}

/**
 * Build a one-off notification for an activity, firing `minutesBefore` the
 * activity's `preferred_time` today. If that time has already passed today,
 * schedule for tomorrow instead (so the user still gets it next occurrence).
 */
function buildActivityReminder(activity) {
  const preferred = parseHHMM(activity.preferred_time);
  const minutesBefore = Math.max(0, parseInt(activity.reminder_minutes_before, 10) || 0);
  if (!preferred || !minutesBefore) return null;

  const now = new Date();
  const target = new Date();
  target.setHours(preferred.hour, preferred.minute, 0, 0);
  target.setTime(target.getTime() - minutesBefore * 60 * 1000);
  if (target.getTime() < now.getTime()) {
    target.setDate(target.getDate() + 1);
  }

  return {
    id: activityIdToNotificationId(activity.activity_id),
    title: activity.name || 'Activity reminder',
    body: minutesBefore >= 60
      ? `Starts in ${Math.round(minutesBefore / 60)}h — get set.`
      : `Starts in ${minutesBefore} min — get set.`,
    schedule: { at: target, allowWhileIdle: true },
    extra: { activity_id: activity.activity_id },
  };
}

/**
 * Schedule (or re-schedule) a single activity's reminder. Cancels any prior
 * notification with the same ID first. Pass an activity with no preferred_time
 * or no reminder_minutes_before to effectively cancel.
 */
export async function reconcileActivityReminder(activity) {
  const LN = await getPlugin();
  if (!LN || !activity || !activity.activity_id) return;
  const id = activityIdToNotificationId(activity.activity_id);
  try {
    await LN.cancel({ notifications: [{ id }] });
  } catch (e) { /* ignore */ }

  const spec = buildActivityReminder(activity);
  if (!spec) return;
  try {
    await LN.schedule({ notifications: [spec] });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('reconcileActivityReminder failed', e);
  }
}

/** Cancel reminder for a specific activity_id (used on delete/archive). */
export async function cancelActivityReminder(activityId) {
  const LN = await getPlugin();
  if (!LN || !activityId) return;
  try {
    await LN.cancel({ notifications: [{ id: activityIdToNotificationId(activityId) }] });
  } catch (e) { /* ignore */ }
}

/**
 * Full reconcile. Called on app-start and after login.
 *   1. Cancel every prior Hourtrix-owned notification (daily slots + activity
 *      reminder range) to clear stale schedules.
 *   2. Re-schedule the 4 daily slots from prefs.
 *   3. Re-schedule one-off reminders for every activity the server knows
 *      about that has both a preferred_time and reminder_minutes_before.
 */
export async function reconcileAllNotifications({ prefs, activities }) {
  const LN = await getPlugin();
  if (!LN) return;

  // Nuke anything Hourtrix might have scheduled previously. Building the list
  // of IDs is cheap and bounded; we don't enumerate the OS-pending list.
  const daily = DAILY_ID_VALUES.map((id) => ({ id }));
  const activityIds = (activities || [])
    .filter((a) => a && a.activity_id)
    .map((a) => ({ id: activityIdToNotificationId(a.activity_id) }));
  try {
    await LN.cancel({ notifications: [...daily, ...activityIds] });
  } catch (e) { /* ignore */ }

  await reconcileDailyReminders(prefs);

  const activityNotifs = (activities || [])
    .map(buildActivityReminder)
    .filter(Boolean);
  if (activityNotifs.length) {
    try {
      await LN.schedule({ notifications: activityNotifs });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('reconcile activities failed', e);
    }
  }
}

/** Debug helper — returns pending notifications on device. No-op on web. */
export async function getPendingNotifications() {
  const LN = await getPlugin();
  if (!LN) return [];
  try {
    const res = await LN.getPending();
    return res.notifications || [];
  } catch (e) {
    return [];
  }
}
